import { useEffect, useState } from "react";
import { Bell, BellRing, BellOff } from "lucide-react";
import { toast } from "sonner";
import {
  addReminder, removeReminder, hasReminderFor, requestNotificationPermission,
  getPermissionState, isNotificationsSupported, LEAD_TIME_LABEL,
} from "../lib/reminders";
import { useReminders } from "./ReminderProvider";

/**
 * Inline bell button to set / cancel a reminder for an upcoming program.
 * Visible only when the program is in the future.
 */
const BELL_STATES = {
  active: {
    Icon: BellRing, label: "Annuler le rappel",
    compactCls: "bg-[color:var(--accent)] text-black border-[color:var(--accent)] shadow-lg",
    fullCls: "bg-[color:var(--accent)] text-black border-[color:var(--accent)] shadow-lg",
  },
  denied: {
    Icon: BellOff, label: "Notifications bloquées",
    compactCls: "bg-black/60 text-red-400 border-red-400/40 opacity-60",
    fullCls: "border-red-400/40 text-red-400 opacity-70",
  },
  idle: {
    Icon: Bell, label: "Me prévenir",
    compactCls: "bg-black/60 text-white border-white/20 hover:bg-[color:var(--accent)] hover:text-black hover:border-[color:var(--accent)]",
    fullCls: "border-[color:var(--border)] text-[color:var(--text-muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]",
  },
};

const bellState = (active, denied) => {
  if (active) return BELL_STATES.active;
  if (denied) return BELL_STATES.denied;
  return BELL_STATES.idle;
};

export const ReminderBell = ({ program, channelName, compact = false }) => {
  const { bump } = useReminders();
  const [active, setActive] = useState(() => hasReminderFor(program.video_id, program.start));
  const [denied, setDenied] = useState(() => getPermissionState() === "denied");

  // Follow external reminder-list updates
  useEffect(() => {
    const onUpdate = () => setActive(hasReminderFor(program.video_id, program.start));
    window.addEventListener("fedevo:reminders-updated", onUpdate);
    return () => window.removeEventListener("fedevo:reminders-updated", onUpdate);
  }, [program.video_id, program.start]);

  if (!isNotificationsSupported()) return null;

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (active) {
      removeReminder(program.video_id, program.start);
      setActive(false);
      bump();
      toast("Rappel annulé", { icon: "🔕" });
      return;
    }
    const perm = await requestNotificationPermission();
    if (perm === "denied") {
      setDenied(true);
      toast.error("Notifications bloquées dans votre navigateur. Activez-les dans les paramètres du site.");
      return;
    }
    if (perm !== "granted") {
      toast.error("Permission refusée");
      return;
    }
    addReminder({
      videoId: program.video_id,
      fireAt: program.start,
      title: program.title,
      channelName,
    });
    setActive(true);
    bump();
    const d = new Date(program.start);
    const hhmm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    toast.success(`Rappel programmé · ${hhmm}`, { description: `${LEAD_TIME_LABEL} · ${program.title}` });
  };

  const ui = bellState(active, denied);
  const Icon = ui.Icon;
  const label = ui.label;

  if (compact) {
    return (
      <button
        type="button"
        onClick={toggle}
        title={label}
        aria-label={label}
        data-testid={`reminder-bell-${program.video_id}`}
        className={`inline-flex items-center justify-center w-6 h-6 rounded-full backdrop-blur-md transition border ${ui.compactCls}`}
      >
        <Icon className="w-3 h-3" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid={`reminder-bell-${program.video_id}`}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[10px] uppercase tracking-widest font-semibold transition ${ui.fullCls}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
};

export default ReminderBell;
